import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { planningsAsync, tripsAsync, tripSlice } from "../trips/tripsSlice";
import {
  selectAdvancedAnalyticsVehiclesList,
  selectAdvancedAnalyticsVehiclesListStatus,
} from "../fleetSlice";
import "../../../../i18n/i18n";
import { SharedInterfaceHistory } from "./sharedInterface";
import {
  authorizationsRejectedByCompanyByPeriodListAsync,
  transactionsListByPeriodAsync,
} from "../../transactions/transactionsSlice";
import { HistoryTripsSidePanel } from "./sidePanel/sidePanel";
import { TripsAndPlanningsHistory } from "../trips/tripsApi";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "../../vehicles/vehiclesSlice";
import {
  getDepartmentsByCompanyAsync,
  selectGetDepartmentsByCompanyStatus,
} from "../../departments/departmentsSlice";

export const HistoryTab = () => {
  const company = useAppSelector(selectCompany);
  const params = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const firstPath = routerLocation.pathname.split("/")[1];
  const dispatch = useAppDispatch();
  const advancedAnalyticsVehicleList = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const advancedAnalyticsVehicleListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, company, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [dispatch, company, getDepartmentsByCompanyStatus]);

  const loadAuthorizationsRejectedByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!company) {
        return;
      }
      void dispatch(
        authorizationsRejectedByCompanyByPeriodListAsync({
          companyUuid: company.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [company, dispatch],
  );

  const loadTransactionByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!company) {
        return;
      }
      void dispatch(
        transactionsListByPeriodAsync({
          companyUuid: company.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [company, dispatch],
  );

  useEffect(() => {
    if (params.tab === "history" && company && !company.hasAdvancedAnalytics) {
      navigate(`/${firstPath}`);
    }
  }, [params.tab, company, navigate, firstPath]);

  const canLoadTripsAndPlannings = useMemo(() => {
    return (
      company &&
      company.hasAdvancedAnalytics &&
      advancedAnalyticsVehicleList &&
      advancedAnalyticsVehicleList.length > 0 &&
      advancedAnalyticsVehicleListStatus === "success"
    );
  }, [
    company,
    advancedAnalyticsVehicleList,
    advancedAnalyticsVehicleListStatus,
  ]);

  const loadPlanningsByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!company || !canLoadTripsAndPlannings) {
        return;
      }
      void dispatch(tripSlice.actions.resetPlanningsListStatus());
      void dispatch(
        planningsAsync({
          companyUuid: company.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [company, canLoadTripsAndPlannings, dispatch],
  );

  const loadTripsByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!company || !canLoadTripsAndPlannings) {
        return;
      }
      void dispatch(tripSlice.actions.resetTripsListStatus());
      void dispatch(
        tripsAsync({
          vehicles: advancedAnalyticsVehicleList,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [company, canLoadTripsAndPlannings, dispatch, advancedAnalyticsVehicleList],
  );

  const hasNoAdvancedAnalyticsVehicles = useMemo(() => {
    return (
      advancedAnalyticsVehicleList.length === 0 &&
      advancedAnalyticsVehicleListStatus === "success"
    );
  }, [advancedAnalyticsVehicleList.length, advancedAnalyticsVehicleListStatus]);

  const sidePanelAction = useCallback(
    (props: { currentTripId: string }) => {
      navigate(
        `/${firstPath}/fleet/history/${props.currentTripId}${routerLocation.search}`,
      );
    },
    [navigate, firstPath, routerLocation.search],
  );

  if (params.tab !== "history") {
    return;
  }

  return (
    <SharedInterfaceHistory
      hasNoAdvancedAnalyticsVehicles={hasNoAdvancedAnalyticsVehicles}
      loadTransactionByPeriodAction={loadTransactionByPeriodAction}
      loadAuthorizationsRejectedByPeriodAction={
        loadAuthorizationsRejectedByPeriodAction
      }
      loadTripsByPeriodAction={loadTripsByPeriodAction}
      loadPlanningsByPeriodAction={loadPlanningsByPeriodAction}
      sidePanelAction={sidePanelAction}
      sidePanel={(props: {
        trip: TripsAndPlanningsHistory;
        previousTripId: string | null;
        nextTripId: string | null;
      }) => (
        <HistoryTripsSidePanel
          tripsAndPlanningsHistory={props.trip}
          previousTripId={props.previousTripId}
          nextTripId={props.nextTripId}
        />
      )}
    />
  );
};
